import * as moment from 'moment';
import * as _ from 'lodash';
import { FormEvent, useMemo, useState } from 'react';
import { gpsListTypeMap, IGpsItemsForList } from '@nkr/models/index';
import { GpsListType } from '@nkr/enums/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { AgGridReact } from 'ag-grid-react';
import { CellClickedEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent } from 'ag-grid-community';
import { GpsAction } from './gps-action';
import { Button, FormControl, Glyphicon, Label } from 'react-bootstrap';
import CheckboxRenderer from '@nkr/components/checkbox-renderer';
import { Utility } from '@nkr/services/utility';
import { GpsTrackingNumberType } from './gps-tracking-number-type';

const actionMap = [
    { type: GpsListType.PendingShip, label: 'Ship GPS', action: GpsAction.AddGpsNumber },
    { type: GpsListType.PendingGps, label: 'Enter Journey Link', action: GpsAction.MarkActive },
    { type: GpsListType.InProcess, label: 'View Journey', action: GpsAction.ViewGpsCentral },
    { type: GpsListType.ReturnToNKR, label: 'Mark Returned', action: GpsAction.MarkShippedBack },
    { type: GpsListType.PendingGps, label: 'Add Tracking Number', action: GpsAction.AddTrackingNumber }
];

interface IProps {
    list: IGpsItemsForList[],
    listType: number,
    centerId: number,
    isAdmin: boolean,
    isAdminLogin: boolean,
    isLoading: boolean,
    onSelectAllRows: () => void,
    onRowSelected: (id: number, isSelected: boolean) => void,
    onGridReady: (e: GridReadyEvent) => void,
    onItemSelected: (id: number, action: GpsAction, trackingType: GpsTrackingNumberType, comment: string) => void
}

const Grid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
        props.onGridReady(e);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        if (props.listType != null) {
            e.api.sizeColumnsToFit();
        }
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = () => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const getListTypeName = () => {
        let listType = gpsListTypeMap.find(x => x.type == props.listType);

        if (listType != null) {
            return listType.name;
        }

        return '';
    };

    const getListTypeNumber = () => {
        let listType = props.listType

        if (listType != null) {
            return listType;
        }
        return 0;
    };

    const handleRowSelected = (id: number, isSelected: boolean) => {
        props.onRowSelected(id, isSelected);
    };

    const handleSelectAllRows = () => {
        props.onSelectAllRows();
    };

    const cols: ColDef[] = [
        {
            colId: 'isSelected',
            field: 'isSelected',
            headerName: '',
            width: 35,
            sortable: false,
            suppressSizeToFit: true,
            cellRendererParams: { onValueChange: handleRowSelected },
            cellRenderer: 'checkboxRenderer',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}><Glyphicon glyph='check' title='Check/Uncheck All Rows' onClick={handleSelectAllRows} /></SortableHeaderComponent>,
        },
        {
            colId: 'id',
            field: 'id',
            sortable: false,
            suppressSizeToFit: true,
            hide: true
        },
        {
            colId: 'rowNumber',
            headerName: 'Row #',
            width: 55,
            sortable: false,
            suppressSizeToFit: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Row #</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.rowIndex + 1}</>
        },
        {
            colId: 'outboundTrackingNumber',
            field: 'outboundTrackingNumber',
            headerName: 'UPS/FEDEX Tracking',
            sortable: true,
            width: 120,
            hide: props.listType == GpsListType.PendingShip,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Outbound Tracking #</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let label = '';
                let gpsAction = GpsAction.AddTrackingNumber;
                let trackingType = GpsTrackingNumberType.OutboundTrackingNumber;

                let l = actionMap.find(x => x.action == gpsAction);

                if (l != null) {
                    label = l.label;
                    gpsAction = l.action
                }

                let data = params.data as IGpsItemsForList;

                let isOverridden = false;
                if (data.outboundTrackingNumber) {
                    label = data.outboundTrackingNumber;
                    isOverridden = true;
                }

                return (
                    <span
                        style={{
                            display: 'block',
                            width: '100%',
                            fontStyle: isOverridden ? 'normal' : 'italic'
                        }}
                        onClick={() => props.onItemSelected(params.data.id, gpsAction, trackingType, '')}
                    >
                        {label}
                    </span>
                );
            }
        },
        {
            colId: 'gpsNumber',
            field: 'gpsNumber',
            headerName: 'GPS #',
            sortable: true,
            width: 100,
            hide: props.isAdmin == false || props.listType == GpsListType.PendingShip,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>GPS #</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IGpsItemsForList;

                return (
                    <>{data.gpsNumber} ({data.vendor})</>
                );
            }
        },
        {
            colId: 'swapNumber',
            field: 'swapNumber',
            headerName: 'Swap #',
            sortable: true,
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Swap #</SortableHeaderComponent>,
        },
        {
            colId: 'transportEmailDate',
            field: 'transportEmailDate',
            headerName: 'Kidney Pickup Date',
            sortable: true,
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Kidney Pickup<br />Date</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IGpsItemsForList;

                if (getListTypeNumber() == GpsListType.InProcess) {

                    return moment(props.data.transportEmailDate).format('MM/DD/YYYY H:mm') + ' ' + props.data.dcTimeZone;
                }
                else
                    return moment(props.data.transportEmailDate).format('MM/DD/YYYY');
            },
        },
        {
            colId: 'kidneyDeliveryDate',
            field: 'kidneyDeliveryDate',
            headerName: 'Kidney Delivery Date',
            sortable: true,
            width: 100,
            hide: props.listType == GpsListType.PendingShip || props.listType == GpsListType.PendingGps || props.listType == GpsListType.InProcess || props.listType == GpsListType.ReturnToNKR,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Kidney Delivery<br />{props.listType == GpsListType.InProcess ? 'Date/Time' : 'Date'}</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IGpsItemsForList;

                if (getListTypeNumber() == GpsListType.InProcess) {

                    return moment(props.data.kidneyDeliveryDate).format('MM/DD/YYYY H:mm') + ' ' + props.data.dcTimeZone;
                }
                else
                    return moment(props.data.kidneyDeliveryDate).format('MM/DD/YYYY');
            }
        },

        {
            colId: 'businessDays',
            field: 'businessDays',
            headerName: 'Business Days Remaining',
            sortable: true,
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>{props.listType == GpsListType.ReturnToNKR ? <>Business Days<br />After Swap</> : <>Business Days<br />Remaining</>}</SortableHeaderComponent>
        },
        {
            colId: 'donorCenter',
            field: 'donorCenter',
            headerName: 'Donor Ctr',
            sortable: true,
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />Center</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                return <><span>{props.data.donorCenter}</span>  {props.data.rdnCenter != "" ? <span style={{ color: 'red', fontWeight: 'bold' }}> ({props.data.rdnCenter})</span> : ""}</>;
            }
        },
        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'Donor Alias',
            sortable: true,
            width: 100,
            hide: props.listType == GpsListType.PendingGps || props.listType == GpsListType.InProcess || props.listType == GpsListType.ReturnToNKR,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor<br />Alias</SortableHeaderComponent>,
        },
        {
            colId: 'recipientCenter',
            field: 'recipientCenter',
            headerName: 'Recipient Ctr',
            sortable: true,
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />Center</SortableHeaderComponent>,
        },
        {
            colId: 'recpAlias',
            field: 'recpAlias',
            headerName: 'Recipient Alias',
            sortable: true,
            width: 100,
            hide: props.listType == GpsListType.PendingGps || props.listType == GpsListType.InProcess || props.listType == GpsListType.ReturnToNKR,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient<br />Alias</SortableHeaderComponent>,
        },
        {
            colId: 'returnTrackingNumber',
            field: 'returnTrackingNumber',
            headerName: 'UPS/FEDEX Tracking',
            sortable: true,
            width: 120,
            hide: props.listType == GpsListType.PendingShip,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Return Tracking #</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let label = '';
                let gpsAction = GpsAction.AddTrackingNumber;
                let trackingType = GpsTrackingNumberType.ReturnTrackingNumber;

                let l = actionMap.find(x => x.action == gpsAction);

                if (l != null) {
                    label = l.label;
                    gpsAction = l.action
                }

                let data = params.data as IGpsItemsForList;

                let isOverridden = false;
                if (data.returnTrackingNumber) {
                    label = data.returnTrackingNumber;
                    isOverridden = true;
                }

                return (
                    <span
                        style={{
                            display: 'block',
                            width: '100%',
                            fontStyle: isOverridden ? 'normal' : 'italic'
                        }}
                        onClick={() => props.onItemSelected(params.data.id, gpsAction, trackingType, '')}
                    >
                        {label}
                    </span>
                );
            },
        },
        {
            colId: 'actionMenu',
            field: 'actionMenu',
            width: 150,
            cellClass: 'action-menu',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Actions</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let label = '';
                let gpsAction = GpsAction.AddGpsLink;

                let l = actionMap.find(x => x.type == props.listType);

                if (l != null) {
                    label = l.label;
                    gpsAction = l.action
                }

                return <Button bsStyle='primary' bsSize='xs' onClick={() => props.onItemSelected(params.data.id, gpsAction, null, '')} >Update Status</Button>;
            }
        }
    ];

    const list = props.list.length > 0 ? props.list : (props.isLoading ? null : []);

    const columnDefs = useMemo(() => cols, []);
    const rowData = useMemo(() => list, [list]);

    const listTypeName = getListTypeName();

    return (
        <AgGridReact
            columnDefs={columnDefs}
            rowData={rowData}
            headerHeight={40}
            rowHeight={30}
            suppressCellSelection={false}
            suppressContextMenu={false}
            overlayNoRowsTemplate={`No [${listTypeName}] gps records...`}
            overlayLoadingTemplate={`Loading [${listTypeName}] gps records...`}
            getRowNodeId={(data: IGpsItemsForList) => data.id.toString()}
            frameworkComponents={{
                checkboxRenderer: CheckboxRenderer
            }}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            onBodyScroll={onBodyScroll}
            onGridSizeChanged={onGridSizeChanged}
            onRowDataChanged={onRowDataChanged}
            onCellClicked={(e: CellClickedEvent) => {
                if (e.colDef.field != 'isSelected' && e.colDef.field != 'actionMenu') {
                }
            }}
            columnHoverHighlight={true}
            getRowStyle={(params: any) => {
                var retcolor = {};

                return retcolor;
            }}
        />
    );
}

export default Grid;